import {
  AfterViewInit,
  Component,
  ElementRef,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ChangeDetectorRef,
  input,
  viewChild,
  output,
} from '@angular/core';
import * as echarts from 'echarts';
import { fmtPaperNo } from '@app/task/task-analysis/task-analysis.component';
import { TaskAnalysisService } from '@app/task/task-analysis/task-analysis.service';
import { TaskAnalysis, TaskQuestionCorrectPercent, TaskTypeEnum } from '@shared/model/task.models';
import { TaskService } from '@shared/service/task.service';
import { TaskStatisItem, sumTaskFeedbackUnread } from '@app/message/message.api.service';
import { getEnvaluateColor, sleep } from '@shared/utils/utils';
import { TabsItem } from '@common';
import { flagBase } from './flag';
import { cloneDeep } from 'es-toolkit/compat';
import { ActivatedRoute } from '@angular/router';

interface TaskQuestionCorrectPercentWithClassroomName extends TaskQuestionCorrectPercent {
  classroomName: string;
}

export enum TabsKeyEnum {
  Question = 'question',
  Module = 'Module',
  Feedback = 'Feedback',
}

@Component({
  selector: 'fl-task-analysis-chart',
  templateUrl: './task-analysis-chart.component.html',
  styleUrls: ['./task-analysis-chart.component.scss'],
  standalone: false,
})
export class TaskAnalysisChartComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {
  private resizeObserver: ResizeObserver;
  // TODO: Skipped for migration because:
  //  Your application code writes to the input. This prevents migration.
  @Input() task: TaskAnalysis;
  /**
   * 当前选中的题号
   */
  readonly currentQuestionNo = input<string>(undefined);
  /** 任务消息子话题统计（unReadCount 用于柱状图未读红点） */
  readonly messageStatis = input<TaskStatisItem[]>([]);
  readonly changeNo = output<any>();
  readonly echartsColumnRef = viewChild<ElementRef>('echartsColumn');
  private echartsInstance: echarts.ECharts;
  // 模块得分率图（ECharts）容器与实例
  readonly echartsBarRef = viewChild<ElementRef>('echartsBar');
  private barEchartsInstance: echarts.ECharts;

  activeTab: TabsKeyEnum = TabsKeyEnum.Question;
  private previousActiveTab: TabsKeyEnum;

  TabsKeyEnum = TabsKeyEnum;

  tabs: TabsItem[] = [];

  correctFilter: number;

  columnTooltip = items => {
    const data = items[0].data;
    return [
      {
        ...items[0],
        name: '题号',
        value: data.no,
      },
      {
        ...items[0],
        name: '得分率',
        value: `${data.correctPercent}%`,
      },
      {
        ...items[0],
        name: '完成人数',
        value: data.finishCount,
      },
      {
        ...items[0],
        name: '错误人数',
        value: data.wrongStudentCount,
      },
    ];
  };
  compareColumnTooltip = items => {
    const data = items[0].data;
    const _data = this.columnOptions.data.filter(i => i.paperQuestionId === data.paperQuestionId);
    return [
      {
        ...items[0],
        name: '题号',
        value: data.no,
      },
      {
        ...items[0],
        name: _data[0].classroomName,
        value: `${_data[0].correctPercent}%`,
      },
      {
        ...items[0],
        name: _data[1].classroomName,
        value: `${_data[1].correctPercent}%`,
      },
    ];
  };
  columnLabelOpts = {
    position: 'inside', // 修改为柱状图内部中间位置
    style: {
      fill: '#fff', // 修改为白色以便在柱状图内部更容易看清
      fontSize: 12,
    },
    formatter: (text, item) => {
      return `${item.correctPercent}%`;
    },
  };
  columnOptions = {
    data: [],
    xField: 'no',
    yField: 'correctPercent',
    appendPadding: [0, 0, 0, 0],
    height: 140,
    isGroup: false,
    groupField: 'classroomName',
    seriesField: 'correctPercent',
    colorField: 'correctPercent',
    legend: false,
    meta: {},
    label: this.columnLabelOpts,
    tooltip: {
      showTitle: false,
      customItems: this.columnTooltip,
    },
    color: ({ correctPercent }) => getEnvaluateColor(correctPercent),
    events: [
      {
        eventName: 'element:click',
        handle: e => {
          this.changeNo.emit(e.data.data);
        },
      },
    ],
  };

  selectChart: string = 'column';
  questionsStatis: TaskQuestionCorrectPercentWithClassroomName[] = [];
  compareQuestionsStatis: TaskQuestionCorrectPercentWithClassroomName[] = [];

  colorMap = {
    unfinished: '#c9c9c9',
    a: '#d92325',
    b: '#ec6941',
    c: '#b3d465',
    d: '#32b16c',
    e: '#18783b',
  };

  compareTaskId: string | number = null;

  get compareClassroom() {
    return this.taskAnalysisSrv.taskPackDetails.filter(i => i.taskId !== this.taskAnalysisSrv.currentTaskId);
  }

  get disabledModule(): boolean {
    if (!this.task) return true;
    return ![TaskTypeEnum.Material, TaskTypeEnum.Course, TaskTypeEnum.Recitation].includes(this.task.type);
  }

  get disabledCorrectPercent(): boolean {
    if (!this.task) return true;
    return [TaskTypeEnum.BookletPersonality, TaskTypeEnum.QuickUptake].includes(this.task.type);
  }

  public taskAnalysisSrv: TaskAnalysisService = inject(TaskAnalysisService);
  private taskSrv: TaskService = inject(TaskService);
  private route = inject(ActivatedRoute);

  private cdr = inject(ChangeDetectorRef);

  ngOnInit() {
    if ([TaskTypeEnum.Material, TaskTypeEnum.Recitation].includes(this.task.type)) this.activeTab = TabsKeyEnum.Module;
    if (this.route.snapshot.queryParamMap.has('activeChatTab'))
      this.activeTab = this.route.snapshot.queryParamMap.get('activeChatTab') as TabsKeyEnum;
    this.initTabs();
  }

  ngAfterViewInit() {
    setTimeout(() => {
      this.initEchartsInstance();
      this.initBarEchartsInstance();

      // 添加窗口大小变化监听
      this.resizeObserver = new ResizeObserver(() => {
        if (this.echartsInstance) {
          this.echartsInstance.resize();
        }
        if (this.barEchartsInstance) {
          this.barEchartsInstance.resize();
        }
      });

      const echartsColumnRef = this.echartsColumnRef();
      if (echartsColumnRef?.nativeElement) {
        this.resizeObserver.observe(echartsColumnRef.nativeElement);
      }
      const echartsBarRef = this.echartsBarRef();
      if (echartsBarRef?.nativeElement) {
        this.resizeObserver.observe(echartsBarRef.nativeElement);
      }
    }, 0);
  }

  ngOnDestroy() {
    if (this.echartsInstance) {
      this.echartsInstance.dispose();
    }
    if (this.barEchartsInstance) {
      this.barEchartsInstance.dispose();
    }

    // 清理ResizeObserver
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  initTabs() {
    this.tabs = [
      {
        name: '题目得分率',
        key: TabsKeyEnum.Question,
        hidden: [TaskTypeEnum.BookletOfPerson, TaskTypeEnum.QuickUptake].includes(this.task?.type),
      },
      {
        name: '模块得分率',
        key: TabsKeyEnum.Module,
        hidden: [TaskTypeEnum.BookletOfPerson, TaskTypeEnum.QuickUptake].includes(this.task?.type),
      },
      {
        name: '任务反馈',
        key: TabsKeyEnum.Feedback,
        disabled: !this.taskAnalysisSrv.isClassroomTask,
      },
    ];
    if ([TaskTypeEnum.BookletOfPerson, TaskTypeEnum.QuickUptake].includes(this.task?.type))
      this.activeTab = TabsKeyEnum.Feedback;
    this.updateFeedbackBadge();
  }

  /**
   * 「任务反馈」角标：进页面就用 messageStatis 算出
   * 反馈组件只在 Feedback tab 激活时才实例化，不能作为角标的唯一来源，否则不点开就永远没有红点
   */
  private updateFeedbackBadge() {
    if (!this.tabs?.length) return;
    this.tabs[2].badge = sumTaskFeedbackUnread(this.messageStatis());
  }

  feedbackNumChange(num: number) {
    this.tabs[2].badge = num;
  }

  private initEchartsInstance() {
    const echartsColumnRef = this.echartsColumnRef();
    if (echartsColumnRef && !this.echartsInstance) {
      this.echartsInstance = echarts.init(echartsColumnRef.nativeElement);
      // 强制执行一次resize以确保图表正确适应容器大小
      setTimeout(() => {
        if (this.echartsInstance) {
          this.echartsInstance.resize();
        }
      }, 0);
    }
  }

  private initBarEchartsInstance() {
    const echartsBarRef = this.echartsBarRef();
    if (echartsBarRef && !this.barEchartsInstance) {
      // 独立于题目图实例，避免 tab 切换时互相影响
      this.barEchartsInstance = echarts.init(echartsBarRef.nativeElement);
      setTimeout(() => {
        if (this.barEchartsInstance) {
          this.barEchartsInstance.resize();
        }
      }, 0);
    }
  }

  onTabChange(tab: TabsKeyEnum | string) {
    this.activeTab = tab as TabsKeyEnum;
    setTimeout(() => {
      // 切换到题目页时仅 resize 题目图
      if (this.activeTab === TabsKeyEnum.Question) {
        this.echartsInstance?.resize();
      }
      // 切换到模块页时重算 option 并 resize，确保隐藏容器恢复后正确布局
      if (this.activeTab === TabsKeyEnum.Module) {
        this.updateBarChart();
        this.barEchartsInstance?.resize();
      }
    }, 0);
  }

  /** 重建柱状图输入：task.questionStatis + messageStatis，discussCount 改为该题未读消息数 */
  private setQuestionsStatis() {
    if (!this.task) return;
    // 该题未读消息数，键为 paperQuestionId
    const unreadByQuestion = new Map<number, number>();
    (this.messageStatis() || []).forEach(s => {
      if (s.paperQuestionId != null) unreadByQuestion.set(s.paperQuestionId, s.unReadCount || 0);
    });
    this.questionsStatis = cloneDeep(this.task.questionStatis).map(i => ({
      ...i,
      classroomName: this.taskAnalysisSrv.currentTaskId
        ? this.taskAnalysisSrv.allTaskPackDetails.find(j => j.taskId === this.taskAnalysisSrv.currentTaskId)
            ?.classroomName
        : this.taskAnalysisSrv.classDirector.find(x => x.classroomId === this.taskAnalysisSrv.currentClassroomId)
            ?.classroomName || '',
      discussCount: unreadByQuestion.get(i.paperQuestionId) ?? 0,
    }));
    // 确保DOM已经渲染完成
    setTimeout(() => {
      this.updateColumnChart();
    }, 0);
  }

  async ngOnChanges(changes: SimpleChanges) {
    for (let key in changes) {
      if (key === 'task' && changes[key].currentValue) {
        this.task = changes[key].currentValue;
        await sleep(100);
        if (
          this.task.questionStatis.length === 0 &&
          ![TaskTypeEnum.BookletOfPerson, TaskTypeEnum.QuickUptake].includes(this.task.type)
        ) {
          this.activeTab = TabsKeyEnum.Module;
        }
        this.setQuestionsStatis();
        // OnPush：输入变更经 await sleep 续体后写入题号统计/切 tab，先标记再更新模块图（echarts 命令式，异常也不影响统计渲染）
        this.cdr.markForCheck();
        if (!this.disabledModule) await this.updateBarChart();
      }
      if (key === 'messageStatis' && changes[key].currentValue && this.task) {
        this.setQuestionsStatis();
        this.updateFeedbackBadge();
      }
    }
    if (changes.hasOwnProperty('currentQuestionNo')) this.updateColumnChart();
  }

  async getTaskQuestionsStatis(taskId: string | number) {
    const statis = await this.taskSrv.getTaskQuestionsStatis(
      typeof taskId === 'number' ? 'special' : (taskId as 'all' | 'my'),
      {
        taskId: typeof taskId === 'number' ? taskId : null,
        taskPackId: typeof taskId === 'number' ? null : this.taskAnalysisSrv.taskPackId,
      }
    );

    // 清空之前的对比数据，避免累积
    this.compareQuestionsStatis = [];

    let compareQuestionsStatis = cloneDeep(statis).map(i => {
      return {
        ...i,
        classroomName: this.compareClassroom.find(j => j.taskId === taskId)?.classroomName || '对比班级',
      };
    });

    if (![TaskTypeEnum.Exercise, TaskTypeEnum.Card, TaskTypeEnum.WorkBook].includes(this.task.type)) {
      compareQuestionsStatis = fmtPaperNo(compareQuestionsStatis);
    }

    this.compareQuestionsStatis = compareQuestionsStatis;
    this.columnOptions.isGroup = true;
    // OnPush：对比班级题目统计经 await 拉取后写入（@Input 传给子组件渲染），非模板事件需主动标记
    this.cdr.markForCheck();

    // 确保DOM已经渲染完成
    setTimeout(() => {
      this.updateColumnChart();
    }, 0);
  }

  /**
   * 处理correctFilter变化，更新图表
   */
  correctChange(e: number) {
    // 确保DOM已经渲染完成
    setTimeout(() => {
      this.updateColumnChart();
    }, 0);
  }

  updateColumnChart() {
    // 如果没有实例，先初始化
    const echartsColumnRef = this.echartsColumnRef();
    if (!this.echartsInstance && echartsColumnRef) {
      this.initEchartsInstance();
    }

    // 如果没有实例，直接返回
    if (!this.echartsInstance) {
      return;
    }

    // 即使没有数据，也确保设置基本的图表样式
    if (this.questionsStatis.length === 0 && this.compareQuestionsStatis.length === 0) {
      const emptyOption: echarts.EChartsOption = {
        grid: {
          top: 10,
          bottom: 10,
          left: 0,
          right: 0,
          containLabel: true,
        },
        xAxis: {
          type: 'category',
          data: [],
          axisLine: {
            lineStyle: {
              color: '#DFE5F0',
            },
          },
        },
        yAxis: {
          type: 'value',
          max: 100,
          axisLabel: {
            formatter: '{value}',
            color: '#CCD4E2',
          },
          splitLine: {
            lineStyle: {
              color: '#EDF2FA',
            },
          },
        },
        series: [],
      };
      this.echartsInstance.setOption(emptyOption, false);
      return;
    }

    // 检查标签页变化，如果从其他标签切换到Question标签，需要重新调整图表大小
    if (this.previousActiveTab !== this.activeTab && this.activeTab === TabsKeyEnum.Question) {
      this.echartsInstance.resize();
    }
    this.previousActiveTab = this.activeTab;

    // 使用Map进行数据去重，提高性能
    const dataMap = new Map();
    // 合并数据并去重
    [...this.questionsStatis, ...this.compareQuestionsStatis].forEach(item => {
      const key = `${item.no}-${item.classroomName}`;
      if (!dataMap.has(key)) {
        dataMap.set(key, item);
      }
    });

    // 获取原始数据
    let processedData = Array.from(dataMap.values());

    // 如果设置了correctFilter，筛选得分率大于等于correctFilter的数据
    if (this.correctFilter !== undefined && this.correctFilter !== null) {
      processedData = processedData.filter(item => item.correctPercent <= this.correctFilter);
    }

    // 保存处理后的数据到columnOptions，以便tooltip使用
    this.columnOptions.data = processedData;

    const isGroup = this.columnOptions.isGroup;
    const series = [];

    // 使用Set提高唯一值获取性能
    const classroomsSet = new Set(processedData.map(item => item.classroomName));
    const uniqueNosSet = new Set(processedData.map(item => item.no));

    const classrooms = Array.from(classroomsSet).filter(name => name && name.trim() !== '');
    const uniqueQuestionNos = Array.from(uniqueNosSet);

    // 预先创建查找映射，避免多次使用find
    const dataLookup = {};
    processedData.forEach(item => {
      if (!dataLookup[item.classroomName]) {
        dataLookup[item.classroomName] = {};
      }
      dataLookup[item.classroomName][item.no] = item;
    });

    // 创建堆叠柱状图系列数据
    if (isGroup) {
      // 分组堆叠柱状图
      classrooms.forEach(classroom => {
        // 得分部分系列（下半部分）
        const correctSeriesData = uniqueQuestionNos.map(no => {
          const item = dataLookup[classroom]?.[no];
          if (item) {
            return {
              value: item.correctPercent,
              correctPercent: item.correctPercent,
              no: item.no,
              paperQuestionId: item.paperQuestionId,
              wrongStudentCount: item.wrongStudentCount,
              classroomName: item.classroomName,
              preferred: item.preferred,
              finishCount: item.finishCount,
              paperId: item.paperId,
              questionId: item.questionId,
              discussCount: item.discussCount,
              // 标记为得分部分
              isCorrectPart: true,
            };
          } else {
            return {
              value: null,
              no: no,
              paperQuestionId: null,
              wrongStudentCount: null,
              classroomName: classroom,
              preferred: false,
              isCorrectPart: true,
            };
          }
        });

        // 未得分部分系列（上半部分）
        const incorrectSeriesData = uniqueQuestionNos.map(no => {
          const item = dataLookup[classroom]?.[no];
          if (item) {
            return {
              value: 100 - item.correctPercent,
              correctPercent: item.correctPercent,
              no: item.no,
              paperQuestionId: item.paperQuestionId,
              wrongStudentCount: item.wrongStudentCount,
              classroomName: item.classroomName,
              preferred: item.preferred,
              finishCount: item.finishCount,
              paperId: item.paperId,
              questionId: item.questionId,
              discussCount: item.discussCount,
              // 标记为未得分部分
              isCorrectPart: false,
            };
          } else {
            return {
              value: null,
              no: no,
              paperQuestionId: null,
              wrongStudentCount: null,
              classroomName: classroom,
              preferred: false,
              isCorrectPart: false,
            };
          }
        });

        // 添加得分部分系列
        series.push({
          name: `${classroom}-得分`,
          type: 'bar',
          stack: classroom, // 使用classroom作为堆叠标识
          itemStyle: {
            borderRadius: [0, 0, 4, 4], // 下半部分：左下角、右下角圆角
          },
          label: {
            show: true,
            position: 'inside',
            formatter: params => {
              return params.value ? `${params.data.correctPercent}%` : '';
            },
            fontSize: 12,
            color: '#fff',
          },
          data: correctSeriesData.map(item => ({
            ...item,
            itemStyle: {
              color: item.value !== null ? getEnvaluateColor(item.correctPercent) : 'transparent',
              borderRadius: [0, 0, 4, 4],
            },
          })),
        });

        // 添加未得分部分系列
        series.push({
          name: `${classroom}-未得分`,
          type: 'bar',
          stack: classroom, // 使用classroom作为堆叠标识
          itemStyle: {
            color: '#EDF2FA', // 灰色填充（与单柱模式保持一致）
            borderRadius: [4, 4, 0, 0], // 上半部分：左上角、右上角圆角
          },
          emphasis: {
            // 禁用 hover 时的颜色变化
            itemStyle: {
              color: '#EDF2FA', // 保持原色不变
            },
          },
          label: {
            show: false, // 上半部分不显示标签
          },
          data: incorrectSeriesData.map(item => ({
            ...item,
            itemStyle: {
              color: '#EDF2FA',
              borderRadius: [4, 4, 0, 0],
            },
            emphasis: {
              // 每个数据项也禁用 hover 颜色变化
              itemStyle: {
                color: '#EDF2FA',
                borderRadius: [4, 4, 0, 0],
              },
            },
          })),
        });
      });
    } else {
      // 普通堆叠柱状图
      // 得分部分系列（下半部分）
      const correctSeriesData = processedData.map(item => ({
        value: item.correctPercent,
        correctPercent: item.correctPercent,
        no: item.no,
        paperQuestionId: item.paperQuestionId,
        wrongStudentCount: item.wrongStudentCount,
        classroomName: item.classroomName,
        preferred: item.preferred,
        finishCount: item.finishCount,
        paperId: item.paperId,
        questionId: item.questionId,
        discussCount: item.discussCount,
        // 标记为得分部分
        isCorrectPart: true,
      }));

      // 未得分部分系列（上半部分）
      const incorrectSeriesData = processedData.map(item => ({
        value: 100 - item.correctPercent,
        correctPercent: item.correctPercent,
        no: item.no,
        paperQuestionId: item.paperQuestionId,
        wrongStudentCount: item.wrongStudentCount,
        classroomName: item.classroomName,
        preferred: item.preferred,
        finishCount: item.finishCount,
        paperId: item.paperId,
        questionId: item.questionId,
        discussCount: item.discussCount,
        // 标记为未得分部分
        isCorrectPart: false,
      }));

      // 添加得分部分系列
      series.push({
        name: '得分部分',
        type: 'bar',
        stack: 'total', // 堆叠标识
        itemStyle: {
          borderRadius: [0, 0, 4, 4], // 下半部分：左下角、右下角圆角
        },
        label: {
          show: true,
          position: 'inside',
          formatter: params => {
            return params.value ? `${params.data.correctPercent}%` : '';
          },
          fontSize: 12,
          color: '#fff',
        },
        data: correctSeriesData.map(item => ({
          ...item,
          itemStyle: {
            color: getEnvaluateColor(item.correctPercent),
            // 当得分率为100%时，只显示上半部分圆角；否则不显示圆角
            borderRadius: [4, 4, 0, 0],
          },
        })),
      });

      // 添加未得分部分系列
      series.push({
        name: '未得分部分',
        type: 'bar',
        stack: 'total', // 堆叠标识
        itemStyle: {
          color: '#EDF2FA', // 灰色填充
          borderRadius: [4, 4, 0, 0], // 上半部分：左上角、右上角圆角
        },
        emphasis: {
          // 禁用hover时的颜色变化
          itemStyle: {
            color: '#EDF2FA', // 保持原色不变
          },
        },
        label: {
          show: false, // 上半部分不显示标签
        },
        data: incorrectSeriesData.map(item => ({
          ...item,
          itemStyle: {
            color: '#EDF2FA',
            // 当得分率为100%时，未得分部分值为0，不显示；否则显示上半部分圆角
            borderRadius: [4, 4, 0, 0],
          },
          emphasis: {
            // 每个数据项也禁用hover颜色变化
            itemStyle: {
              color: '#EDF2FA',
              borderRadius: [4, 4, 0, 0],
            },
          },
        })),
      });
    }

    // 预先创建X轴数据，避免重复计算
    const xAxisData = uniqueQuestionNos.map(no => {
      // 查找对应的题目数据以获取 discussCount
      const questionData = processedData.find(item => item.no === no);
      const discussCount = questionData?.discussCount || 0;

      // 根据 discussCount 动态设置 width
      const baseWidth = discussCount > 0 ? 60 : 40;

      return {
        value: no,
        textStyle:
          this.currentQuestionNo() === no
            ? {
                color: '#0065FD',
                backgroundColor: '#E5F0FF',
                width: baseWidth,
                height: 32,
                borderRadius: 4,
                fontSize: 14,
                fontWeight: 500,
                borderColor: '#BEDAFF',
                borderWidth: 1,
              }
            : {
                color: '#4C576C',
                width: baseWidth,
                height: 32,
                fontSize: 14,
                fontWeight: 500,
              },
      };
    });

    // 预先创建一个查找映射，用于xAxis formatter
    const preferredMap = {};
    processedData.forEach(item => {
      if (!preferredMap[item.no]) {
        preferredMap[item.no] = item.preferred || false;
      }
    });

    // 创建tooltip格式化函数 - 针对堆叠柱状图优化
    const tooltipFormatter = (params: any) => {
      const data = params.data;

      // 如果没有数据，显示无数据提示
      if (!data || data.value === null) {
        return `题号: ${data.no}<br/>该班级无此题数据`;
      }

      // 无论是得分部分还是未得分部分，都显示得分率的tooltip内容
      let tooltipContent = `题号: ${data.no}<br/>得分率: ${data.correctPercent ?? 0}%`;

      if (isGroup) {
        // 在对比模式下，显示班级信息
        tooltipContent = `题号: ${data.no}<br/>班级: ${data.classroomName}<br/>得分率: ${data.correctPercent ?? 0}%`;
      } else {
        tooltipContent += `<br/>完成人数: ${data.finishCount}<br/>错误人数: ${data.wrongStudentCount}`;
      }

      return tooltipContent;
    };

    // 计算图表所需的最小宽度，确保每个柱子至少有24px宽度
    // 计算柱子数量
    const totalBars = uniqueQuestionNos.length * (isGroup ? classrooms.length : 1);
    // 计算图表所需的最小宽度 = 柱子数量 * 最小柱宽(24px) * 1.5(考虑间距)
    const minChartWidth = Math.max(totalBars * 28 * 1.5, 100);

    // 创建ECharts配置
    const option: echarts.EChartsOption = {
      grid: {
        top: 10, // 移除顶部边距使图表占满canvas
        bottom: 10,
        left: 0,
        right: 0,
        containLabel: true, // 保持containLabel为true确保标签仍然可见
      },
      tooltip: {
        trigger: 'item',
        appendTo: document.getElementById('chart-container'),
        extraCssText: 'z-index: 10;',
        formatter: tooltipFormatter,
        confine: true, // 限制tooltip在图表区域内
      },
      xAxis: {
        type: 'category',
        data: xAxisData,
        axisLabel: {
          interval: 0,
          lineHeight: 32,
          margin: 12,
          formatter: value => {
            // 使用预先创建的映射快速查找
            const preferred = preferredMap[value];
            // 查找对应题目的 discussCount
            const questionData = processedData.find(item => item.no === value);
            const discussCount = questionData?.discussCount || 0;
            // 使用统一的rich文本格式，避免高度不一致问题
            // 如果没有反馈数量或为0，则不显示 redDot
            if (discussCount > 0) {
              return preferred ? `{flag|}{redDot|${discussCount}}` : `{normal|${value}}{redDot|${discussCount}}`;
            } else {
              return preferred ? `{flag|}` : `{normal|${value}}`;
            }
          },
          rich: {
            flag: {
              backgroundColor: {
                image: flagBase,
              },
              height: 16,
              width: 16,
              align: 'center',
              verticalAlign: 'middle',
            },
            normal: {
              backgroundColor: 'rgba(0,0,0,0)', // 完全透明
              padding: [10, 10],
              borderRadius: 4,
              height: 20,
              align: 'center',
              verticalAlign: 'middle',
            },
            redDot: {
              backgroundColor: '#ff0000', // 红色背景
              color: '#fff', // 白色文字
              borderRadius: [10, 10, 10, 0], // 圆形
              fontSize: 10,
              fontWeight: 'bold',
              align: 'center',
              verticalAlign: 'middle',
              width: 16,
              height: 16,
            },
          },
        },
        axisLine: {
          lineStyle: {
            color: '#DFE5F0',
          },
        },
        triggerEvent: true,
      },
      yAxis: {
        type: 'value',
        max: 100,
        axisLabel: {
          formatter: '{value}',
          color: '#CCD4E2',
        },
        splitLine: {
          lineStyle: {
            color: '#EDF2FA', // 半透明的分割线
          },
        },
      },
      legend: { show: false },
      series: series,
      animation: false, // 禁用动画以提高性能
    };

    // 设置图表容器的最小宽度，确保有足够空间显示所有柱子
    if (echartsColumnRef && echartsColumnRef.nativeElement) {
      echartsColumnRef.nativeElement.style.minWidth = `${minChartWidth}px`;
    }

    // 使用notMerge=false进行增量更新，提高性能
    this.echartsInstance.setOption(option, false);

    // 优化事件处理，减少重复绑定
    this.echartsInstance.off('click');
    this.echartsInstance.on('click', params => {
      // 处理堆叠柱状图的点击事件
      if (!params.targetType || params.targetType !== 'axisLabel') {
        // 对于堆叠柱状图，无论点击得分部分还是未得分部分，都传递原始数据
        const data = params.data as any;
        const originalData = data.originalData || data;
        this.changeNo.emit(originalData);
      }
    });
    this.echartsInstance.on('click', 'xAxis.category', params => {
      // 使用预先创建的映射快速查找数据
      for (const item of processedData) {
        if (item.no === params.value) {
          this.changeNo.emit(item);
          break;
        }
      }
    });
  }

  async updateBarChart() {
    if (!this.task?.moduleStatis?.length || this.disabledModule) {
      return;
    }

    const echartsBarRef = this.echartsBarRef();
    if (!this.barEchartsInstance && echartsBarRef) {
      this.initBarEchartsInstance();
    }
    if (!this.barEchartsInstance) {
      return;
    }

    // 模块图的所有数据处理统一在 prepareModuleStatis 中完成
    const { titles, groups, valueMap, xMax, moduleMeta } = this.prepareModuleStatis();

    if (echartsBarRef?.nativeElement) {
      // 模块数增多时动态抬高容器，避免柱子挤压或不可见
      echartsBarRef.nativeElement.style.height = `${Math.max(240, titles.length * 52)}px`;
    }

    // 横向堆叠柱：x 为人数值，y 为模块名，series 按 group 堆叠
    const option: echarts.EChartsOption = {
      grid: {
        top: 10,
        right: 24,
        bottom: 60,
        left: 120,
        containLabel: true,
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow',
        },
        formatter: params => {
          const items = Array.isArray(params) ? params : [params];
          const title = items[0]?.axisValueLabel || '';
          const lines = [`${title}`];
          for (const item of items) {
            const value = Number(item.value) || 0;
            lines.push(`${item.marker}${item.seriesName}: ${value}`);
          }

          const meta = moduleMeta.get(title);
          if (meta) {
            const markerBySeries = new Map<string, string>();
            for (const item of items) {
              markerBySeries.set(item.seriesName, item.marker || '');
            }
            if (meta.type === 2 && meta.finishCount && typeof meta.correctPercent === 'number') {
              let scoreGroup = '95-100%';
              if (meta.correctPercent < 60) {
                scoreGroup = '0-60%';
              } else if (meta.correctPercent < 75) {
                scoreGroup = '60-75%';
              } else if (meta.correctPercent < 85) {
                scoreGroup = '75-85%';
              } else if (meta.correctPercent < 95) {
                scoreGroup = '85-95%';
              }
              lines.push(`${markerBySeries.get(scoreGroup) || ''}得分率: ${meta.correctPercent.toFixed(1)}%`);
            }
          }

          return lines.join('<br/>');
        },
      },
      legend: {
        type: 'scroll',
        bottom: 0,
      },
      xAxis: {
        type: 'value',
        min: 0,
        max: xMax > 0 ? xMax : undefined,
        axisLabel: {
          color: '#8592A6',
        },
        axisLine: {
          lineStyle: {
            color: '#DFE5F0',
          },
        },
        splitLine: {
          lineStyle: {
            color: '#EDF2FA',
          },
        },
      },
      yAxis: {
        type: 'category',
        data: titles,
        axisLabel: {
          color: '#4C576C',
        },
        axisLine: {
          lineStyle: {
            color: '#DFE5F0',
          },
        },
      },
      series: groups.map(group => ({
        name: group,
        type: 'bar',
        // 同一模块下各分段进行堆叠
        stack: 'module-total',
        emphasis: {
          focus: 'series',
        },
        itemStyle: {
          color: this.colorMap[this.getType(group)],
        },
        data: titles.map(title => valueMap.get(title)?.get(group) || 0),
      })),
      animation: false,
    };

    // 等待容器尺寸稳定后再 setOption，降低隐藏/切换时布局异常概率
    await sleep(30);
    this.barEchartsInstance.setOption(option, true);
    this.barEchartsInstance.resize();
  }

  private prepareModuleStatis() {
    const emptyResult = {
      titles: [] as string[],
      groups: [] as string[],
      valueMap: new Map<string, Map<string, number>>(),
      xMax: 0,
      moduleMeta: new Map<string, any>(),
    };
    const moduleStatis = this.task?.moduleStatis || [];
    if (!moduleStatis.length) return emptyResult;

    const studentCount = this.task.studentCount || 0;
    const scoreRanges = ['0-60%', '60-75%', '75-85%', '85-95%', '95-100%'];
    const detailRows = [];

    const buildRightLabel = item => {
      const base = `完成率: ${item.finishPercent.toFixed(1)}&||&${item.moduleId}&||&`;
      const scorePart = item.type === 2 && item.finishCount ? `得分率: ${item.correctPercent.toFixed(1)}` : '';
      return `${base}${scorePart}&|&${item.title}`;
    };

    const buildRow = (item, group, value) => ({
      title: item.title,
      group,
      value,
      correctPercent: item.correctPercent,
      finishPercent: item.finishPercent,
      rightLabel: buildRightLabel(item),
    });

    // 按原始展示顺序倒序处理模块
    for (let i = moduleStatis.length - 1; i >= 0; i--) {
      const item = moduleStatis[i];
      const statis = item.statis || [];
      let exerciseFinishCount = 0;

      if (item.type === 2) {
        detailRows.push(buildRow(item, '已完成', 0));
      }

      for (const s of statis) {
        if (item.type === 1 && s.group === '未完成') {
          for (const range of scoreRanges) {
            detailRows.push(buildRow(item, range, 0));
          }
        }

        if ([1, 2, 4, 5].includes(item.type)) {
          const value = s.value || 0;
          detailRows.push(buildRow(item, s.group, value));
          if (item.type === 2) {
            exerciseFinishCount += value;
          }
        }
      }

      if (item.type === 2) {
        detailRows.push(buildRow(item, '未完成', Math.max(0, studentCount - exerciseFinishCount)));
      }
    }

    const groupPriority = ['已完成', '0-60%', '60-75%', '75-85%', '85-95%', '95-100%', '未完成'];
    const titlesSet = new Set<string>();
    const groupsSet = new Set<string>();
    const valueMap = new Map<string, Map<string, number>>();

    for (const row of detailRows) {
      const title = row.title || '';
      const group = row.group || '';
      titlesSet.add(title);
      groupsSet.add(group);
      if (!valueMap.has(title)) valueMap.set(title, new Map<string, number>());
      const groupMap = valueMap.get(title);
      if (!groupMap) continue;
      groupMap.set(group, (groupMap.get(group) || 0) + (row.value || 0));
    }

    const titles = Array.from(titlesSet);
    const groups = [
      ...groupPriority.filter(group => groupsSet.has(group)),
      ...Array.from(groupsSet).filter(group => !groupPriority.includes(group)),
    ];

    let xMax = 0;
    for (const title of titles) {
      const groupMap = valueMap.get(title);
      if (!groupMap) continue;
      const total = Array.from(groupMap.values()).reduce((sum, value) => sum + (value || 0), 0);
      if (total > xMax) xMax = total;
    }

    const moduleMeta = new Map<string, any>();
    for (const item of moduleStatis) {
      if (!moduleMeta.has(item.title)) moduleMeta.set(item.title, item);
    }

    return { titles, groups, valueMap, xMax, moduleMeta };
  }

  private getType(obj) {
    // 使用映射表代替多个 if-else 条件判断，提高性能
    const key = obj.group || obj;
    const typeMap = {
      未完成: 'unfinished',
      '0-60%': 'a',
      '60-75%': 'b',
      '75-85%': 'c',
      '85-95%': 'd',
      '95-100%': 'e',
      已完成: 'e',
    };

    return typeMap[key];
  }
}
